/* eslint
	@typescript-eslint/no-unsafe-call: "off" -- types missing in eslint for lensfun-wasm
*/
import type { PhotoMetadataRecord } from "@/lib/catalog"
import focuslib from "@/lib/raw/focuslib"
import { RawInfo } from "@/lib/raw/fastlib"
import type { vec2, vec3, vec4 } from "gl-matrix"
import { Sentry } from "@/telemetry/sentry"

/* Types */

export type ComputedCameraLensType =
	| "rectilinear"
	| "fisheye"
	| "panoramic"
	| "orthographic"
	| "stereographic"
	| "unknown"

export type ComputedCameraDistortionModel = "ptlens" | "acm" | "unknown"
export type ComputedCameraChromaticAberrationModel = "poly3" | "lineqar" | "acm" | "none"

export interface ComputedEmbeddedCameraLensCorrectionData {
	data: Float32Array
	scaleFactor: number
	hasDistortionCorrection: boolean
	hasVignetteCorrection: boolean
}

export interface ComputedCameraLensCorrectionData {
	type: ComputedCameraLensType

	autoscaleFactor: number
	normalizationScale: number

	imageCenter: vec2

	vignetteParam: vec3 | null
	distortionParam: vec4 | null

	distortionModel: ComputedCameraDistortionModel

	chromaticAberrationParam: number[] | null
	chromaticAberrationModel: ComputedCameraChromaticAberrationModel
}

export interface CameraLensCorrectionData {
	param: number[] // Up to 4 entries (can be empty)
	fLenX: number
	fLenY: number
	imageCenterX: number
	imageCenterY: number
	scaleFactor: number
	meanError: number
}

export interface CameraLensCorrectionModel {
	fLen: number
	fDist: number
	aperture: number

	base: CameraLensCorrectionData // Used for distortion
	chromRG: CameraLensCorrectionData
	chromG: CameraLensCorrectionData
	chromBG: CameraLensCorrectionData
	vignette: CameraLensCorrectionData
}

export interface CameraLensProfile {
	/* Metadata */
	shortName: string
	name: string
	lensName: string
	/* Sensor */
	sensorFormatFactor: number
	/* Perspective correction models */
	models: CameraLensCorrectionModel[]
}

/* Lensfun */

let lensfun: focuslib

export const getLensfunTotalBytesLength = () => {
	return typeof lensfun !== "undefined" ? (lensfun.HEAPU8.byteLength as number) : 0
}

export const getLensProfile = async (
	info: RawInfo,
	metadata: PhotoMetadataRecord | undefined,
): Promise<ComputedCameraLensCorrectionData | undefined> => {
	if (!lensfun) {
		lensfun = await focuslib({
			locateFile: () => new URL("@assets/focuslib.wasm", import.meta.url).href,
		})
	}

	try {
		const data = await lensfun.getCorrection(
			typeof metadata?.cameraMake === "string" ? metadata.cameraMake : "",
			typeof metadata?.cameraModel === "string" ? metadata.cameraModel : "",
			typeof metadata?.lensModel === "string" ? metadata.lensModel : "",
			metadata?.focalLength ?? 0,
			metadata?.fNumber ?? 0,
			metadata?.focusDistance ?? 0,
			info.width,
			info.height,
		)

		const distortionParam =
			data.distortion.isAvailable === true ? (data.distortion.terms as vec4) : null

		let vignetteParam: vec3 | null = null

		if (
			data.vignetting.isAvailable === true &&
			data.vignetting.terms.every((factor) => factor === 0) === false
		) {
			vignetteParam = data.vignetting.terms as vec3
		}

		let chromaticAberrationParam: number[] | null = null

		if (
			data.chromaticAberration &&
			data.chromaticAberration.isAvailable === true &&
			data.chromaticAberration.terms.every((factor) => factor === 0) === false
		) {
			chromaticAberrationParam = data.chromaticAberration.terms
		}

		if (!distortionParam && !vignetteParam) {
			return undefined
		}

		const result = {
			type: data.type,
			imageCenter: [data.centerX, data.centerY] as vec2,

			normalizationScale: data.normalizationScale,
			autoscaleFactor: data.autoscaleFactor,

			vignetteParam,
			distortionParam,

			distortionModel: data.distortion.model,

			chromaticAberrationParam,
			chromaticAberrationModel: data.chromaticAberration?.model ?? null,
		}

		return result
	} catch (e) {
		console.warn(e)
		Sentry.captureException(e, { level: "warning" })
		return undefined
	}
}
